import { DefaultValues } from "react-hook-form";
import * as yup from "yup";
import { UnitOfMeasure } from "../../../../common/enums";
import { ICategorizableItem, INomenclatureCategory } from "../../../../common/nomenclatures/models/categorizedNomenclatureModels";

/**
 * Form values for an inventory item. Fields align with
 * InventoryItemInputModel on the backend.
 *
 * NOTE: `unitOfMeasure` and `initialQuantity` are set at creation and
 * disabled on edit. `currentQuantity` is read-only and only mutated via
 * transfers, sales, or manual adjustment (never via this form).
 */
export const inventoryItemSchema = yup
  .object({
    id: yup
      .number()
      .transform((value, originalValue) =>
        String(originalValue).trim() === "" ? undefined : value
      )
      .optional(),
    name: yup.string().required("Името е задължително").defined(),
    sku: yup.string().nullable().optional(),
    barcode: yup.string().nullable().optional(),
    brand: yup.string().nullable().optional(),
    description: yup.string().nullable().optional(),
    price: yup
      .number()
      .typeError("Цената е задължителна")
      .min(0, "Цената не може да е отрицателна")
      .required("Цената е задължителна")
      .defined(),
    costPrice: yup
      .number()
      .typeError("Себестойността е задължителна")
      .min(0, "Себестойността не може да е отрицателна")
      .required("Себестойността е задължителна")
      .defined(),
    unitOfMeasure: yup
      .number()
      .oneOf([UnitOfMeasure.Piece, UnitOfMeasure.Gram])
      .required("Изберете мерна единица")
      .defined(),
    initialQuantity: yup
      .number()
      .typeError("Началното количество е задължително")
      .min(0, "Количеството не може да е отрицателно")
      .required("Началното количество е задължително")
      .defined(),
    lowStockThreshold: yup
      .number()
      .typeError("Прагът за ниска наличност е задължителен")
      .min(0, "Прагът не може да е отрицателен")
      .required("Прагът за ниска наличност е задължителен")
      .defined(),
    categoryId: yup.number().positive().min(0).nullable().defined(),
    sortOrder: yup.number().optional(),
    isDeleted: yup.boolean().optional(),
    // Read-only in the form but kept so the list row can show it.
    currentQuantity: yup.number().optional(),
    notesCount: yup.number().optional(),
  })
  .required();

export type IInventoryItemInputModel = yup.InferType<typeof inventoryItemSchema> & ICategorizableItem;

export const getInventoryItemDefaults = (): DefaultValues<IInventoryItemInputModel> => ({
  name: "",
  sku: "",
  barcode: "",
  brand: "",
  description: "",
  price: 0,
  costPrice: 0,
  unitOfMeasure: UnitOfMeasure.Piece,
  initialQuantity: 0,
  lowStockThreshold: 5,
  categoryId: null,
});

/**
 * Form values for an inventory category. Includes the required emoji.
 */
export const inventoryCategorySchema = yup
  .object({
    id: yup.number().optional(),
    name: yup.string().required("Името е задължително").defined(),
    emoji: yup
      .string()
      .required("Emoji-то е задължително")
      .min(1, "Emoji-то е задължително")
      .defined(),
  })
  .required();

export type IInventoryCategoryFormModel = yup.InferType<typeof inventoryCategorySchema>;

export const getInventoryCategoryDefaults = (): DefaultValues<IInventoryCategoryFormModel> => ({
  name: "",
  emoji: "",
});

/**
 * Backend shape for an inventory category returned by the API.
 * Extends INomenclatureCategory with the required emoji field.
 */
export interface IInventoryCategoryModel extends INomenclatureCategory {
  emoji: string;
}
